import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { selectSite, selectRole, selectUserGroup } from '../../../store/authReducer';
import { selectSiteSettingsSite } from '../../settings/reducer';
import { hasRole, RoleTypes } from '../../../core/components/Permission';
import {
  findPermissionRef,
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { Files } from '../../../core/components/Files';
import FileUploadDialog from '../../../core/components/FileUploadDialog';
import { apiError } from '../../../core/toastMessages';
import {
  TaskTemplate,
  taskTemplatesApi,
  useGetTaskTemplateMediaQuery,
  useGetTaskTemplateMediaForGroupQuery,
  useArchiveTaskTemplateMediaMutation,
  useArchiveTaskTemplateMediaForGroupMutation,
} from '../../../core/api/rtk/taskTemplatesApi';
import { postTaskTemplateMedia, postTaskTemplateMediaForGroup } from '../../../core/api/files';

interface TaskMediaTabProps {
  task?: TaskTemplate | null;
}

export const TaskMediaTab = ({ task }: TaskMediaTabProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedSiteId = useSelector(selectSite) as number | null;
  const siteSettingsSite = useSelector(selectSiteSettingsSite) as {
    groupId?: number | null;
  } | null;
  const groupId: number | null = siteSettingsSite?.groupId ?? null;
  const role = useSelector(selectRole);
  const userGroup = useSelector(selectUserGroup);

  const enhancedRole = findPermissionRef(
    (userGroup as any)?.permissions ?? [],
    viewPermissionRefs.admin
  )
    ? 'Admin'
    : role;

  const templateId = task?.id ?? null;
  const isGroupTemplate = task?.siteId == null && task?.groupId != null;

  const { data: siteMedia = [] } = useGetTaskTemplateMediaQuery(templateId as number, {
    skip: !templateId || isGroupTemplate,
  });
  const { data: groupMedia = [] } = useGetTaskTemplateMediaForGroupQuery(
    { groupId: groupId as number, taskTemplateId: templateId as number },
    { skip: !templateId || !isGroupTemplate || groupId == null }
  );
  const media = isGroupTemplate ? groupMedia : siteMedia;

  const [archiveMedia] = useArchiveTaskTemplateMediaMutation();
  const [archiveGroupMedia] = useArchiveTaskTemplateMediaForGroupMutation();

  const requiredRole = isGroupTemplate ? RoleTypes.OperatorGroupAdmin : RoleTypes.SiteManager;
  const canModify =
    hasRole(enhancedRole, requiredRole) && (isGroupTemplate ? groupId != null : !!selectedSiteId);

  const [uploadOpen, setUploadOpen] = useState(false);

  const handleAdd = async (file: File): Promise<void> => {
    if (!templateId) return;
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (isGroupTemplate && groupId != null) {
      await postTaskTemplateMediaForGroup({
        options: { groupId },
        data: { taskTemplateId: templateId, file: formData },
      });
    } else {
      if (!selectedSiteId) throw new Error('No site selected');
      await postTaskTemplateMedia({
        options: { siteId: selectedSiteId },
        data: { taskTemplateId: templateId, file: formData },
      });
    }
    dispatch(
      taskTemplatesApi.util.invalidateTags([
        { type: 'TaskTemplate' as const, id: `task-template-media-${templateId}` },
      ])
    );
  };

  const handleDelete = async (file: any): Promise<void> => {
    if (!templateId) return;
    try {
      if (isGroupTemplate && groupId != null) {
        await archiveGroupMedia({ groupId, taskTemplateId: templateId, fileId: file.id }).unwrap();
      } else {
        await archiveMedia({ taskTemplateId: templateId, fileId: file.id }).unwrap();
      }
    } catch (e: any) {
      apiError(e?.data || 'Failed to remove media');
    }
  };

  return (
    <div className="new-videos-tab">
      {canModify && (
        <button
          className="button"
          type="button"
          onClick={() => setUploadOpen(true)}
          style={{ marginBottom: '0.75rem' }}
        >
          + Upload
        </button>
      )}
      <FileUploadDialog
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={handleAdd}
        submitButtonText={intl.formatMessage({ id: 'uploadMedia' })}
        fileType={['image', 'video']}
        instructions={intl.formatMessage({ id: 'uploadPhotoVideoOnly' })}
        multipleFiles
      />
      <Files
        files={media}
        addFileFunction={() => {}}
        deleteFileFunction={handleDelete}
        canAdd={false}
        canDelete={canModify}
        multipleFiles
      />
    </div>
  );
};
